import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight, faArrowUpFromBracket, faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { AutoComplete, Button, Card, Dropdown, Input, message, Modal, Select, Spin, Table, Tabs } from "antd";
import { Option } from "antd/es/mentions";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import WarehouseFilter from "../components/Filters/WarehouseFilter";
import useDebounced from "../customHooks/useDebounced";
import dayjs from "../helpers/dayjsSetup";
import { useGlobalFilters } from "../providers/GlobalFiltersProvider";
import ContainerService from "../services/Container/ContainerService";
import ShipmentService from "../services/Shipment/ShipmentService";
import AppButton from "../common/AppButton";

const ContainerOnSiteList = ({ warehouses }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { filters } = useGlobalFilters();
    const [activeTab, setActiveTab] = useState(location?.state?.activeTab || "onSite");
    const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
    const [searchInput, setSearchInput] = useState(
        location?.state?.selectedContainerNum
            ? `${location?.state?.selectedContainerNum} - ${location?.state?.activeTab.replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}`
            : "",
    );
    const debouncedSearch = useDebounced(searchInput, 500);
    const [list, setList] = useState({
        request: {
            containerNum: location?.state?.selectedContainerNum || null,
            pageFilter: { all: false, size: 50, num: 0 },
            sortFilter: { field: "", order: "" },
        }
    });

    const { containerNum, pageFilter, sortFilter } = list.request;

    const { data: counts = {} } = useQuery({
        queryKey: ["shipmentCounts", containerNum, pageFilter.num, pageFilter.size, sortFilter.field, sortFilter.order, filters],
        queryFn: async () => ShipmentService.fetchShipmentDataCount({ ...list.request, ...filters })
    });

    const { data: shipmentData, isFetching: loading, } = useQuery({
        queryKey: ["shipments", activeTab, containerNum, pageFilter.num, pageFilter.size, sortFilter.field, sortFilter.order, filters],
        queryFn: async () => ShipmentService.fetchShipmentData(activeTab, { ...list.request, ...filters }),
        staleTime: 5 * 60 * 1000
    });

    const tabs = [
        "onSite",
    ];

    useEffect(() => {
        const defaultSorts = {
            onSite: { field: "deliveryDate", order: "desc" }, // Latest delivery first
        };

        setList((prev) => ({
            ...prev,
            request: {
                ...prev.request,
                pageFilter: { ...prev.request.pageFilter, num: 0 },
                sortFilter: defaultSorts[activeTab] || { field: "", order: "" },
            },
        }));
    }, [activeTab]);

    const leftAlign = { align: "left" };
    const rightAlign = { align: "right" };
    const centerAlign = { align: "center" };

    const formatDate = (value) =>
        value ? dayjs(value).format("DD MMM YYYY") : "-";

    const dateColumn = (
        title,
        dataIndex,
        classNames = "w-28",
        defaultSortOrder = "",
    ) => ({
        key: dataIndex,
        title,
        dataIndex,
        ...leftAlign,
        render: (value) => <div className={classNames}>{formatDate(value)}</div>,
        sorter: (a, b) => new Date(a[dataIndex]) - new Date(b[dataIndex]),
        defaultSortOrder,
    });

    const leftColumn = (
        title,
        dataIndex,
        customRender,
        sortable = false,
        classNames = "w-28",
    ) => ({
        key: dataIndex,
        title,
        dataIndex,
        ...leftAlign,
        render:
            customRender ||
            ((text) => (
                <div className={classNames}>
                    {typeof text === "boolean" ? (text ? "Yes" : "No") : text}
                </div>
            )),
        sorter: sortable
            ? (a, b) => {
                const valA = a[dataIndex];
                const valB = b[dataIndex];

                if (!isNaN(valA) && !isNaN(valB)) {
                    return Number(valA) - Number(valB);
                }

                return (valA || "").toString().localeCompare((valB || "").toString());
            }
            : false,
    });

    const handleView = (record) => {
        navigate("/shipment-gateway/shipment-details", {
            state: {
                pathname: location.pathname,
                containerID: record.containerID,
                activeTab,
                selectedContainerNum: list.request.containerNum,
            },
        });
    };

    const rawSearch = debouncedSearch?.split(" - ")[0]?.trim();

    const { data: searchResults = [], isFetching: searchLoading, } = useQuery({
        queryKey: ["containerSearch", rawSearch, filters, "showOnSite"],
        queryFn: () => ContainerService.searchContainerWithFilters({ containerNum: rawSearch,showOnSite:true, ...filters }),
        enabled: !!rawSearch,
        staleTime: 5 * 60 * 1000,
    });

    const handleSearchInputChange = (value) => {
        setSearchInput(value);
    };

    const handleContainerSelect = (selectedContainer) => {
        setSearchInput("");
        const { tabName, containerNum } = selectedContainer;
        setIsSearchModalVisible(false);
        // setActiveTab(tabName);
        setList((prev) => ({
            ...prev,
            request: { ...prev.request, containerNum },
        }));
    };

    const handleCancelModal = () => {
        setSearchInput("");
        setIsSearchModalVisible(false);
    };

    const handleClear = () => {
        setSearchInput("");
    };

    const handleClearSearchResult = () => {
        setSearchInput("");
        setList((prev) => ({
            ...prev,
            request: { ...prev.request, containerNum: null, },
        }));
    };

    const currentField = list.request.sortFilter.field;
    const currentOrder =
        list.request.sortFilter.order === "asc"
            ? "ascend"
            : list.request.sortFilter.order === "desc"
                ? "descend"
                : null;

    const columnsMap = {
        onSite: [
            {
                ...leftColumn(
                    <div className="ml-12">Container</div>,
                    "containerNumber",
                    (_, record) => (
                        <div className="ml-8">
                            <Button
                                className="w-36 overflow-hidden whitespace-nowrap font-bold text-sm"
                                type="primary"
                                onClick={() => handleView(record)}
                            >
                                {record.containerNumber}
                            </Button>
                        </div>
                    ),
                    true,
                    "w-36" // Explicit width to match Button
                ),
                sortOrder: currentField === "containerNumber" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    "Type",
                    "containerType",
                    (value) => <div className="w-24">{value}</div>,
                    true,
                    "w-24"
                ),
                sortOrder: currentField === "containerType" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    "Supplier",
                    "supplierShort",
                    (value) => <div className="w-36">{value}</div>,
                    true,
                    "w-36"
                ),
                sortOrder: currentField === "supplierShort" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    "Reference",
                    "reference",
                    (value) => <div className="w-36">{value}</div>,
                    true,
                    "w-36"
                ),
                sortOrder: currentField === "reference" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    "Contents",
                    "contentsShort",
                    (value) => <div className="w-36">{value}</div>,
                    true,
                    "w-36"
                ),
                sortOrder: currentField === "contentsShort" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    "Status",
                    "status",
                    (value) => <div className="w-36">{value}</div>,
                    true,
                    "w-36"
                ),
                sortOrder: currentField === "status" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    "Warehouse",
                    "warehouseName",
                    (value) => <div className="w-36">{value}</div>,
                    true,
                    "w-36"
                ),
                sortOrder: currentField === "warehouseName" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    <div>
                        Receiving
                        <br />
                        Zone
                    </div>,
                    "receivingZoneName",
                    (value) => <div className="w-36">{value}</div>,
                    true,
                    "w-36"
                ),
                sortOrder: currentField === "receivingZoneName" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    "Dock",
                    "dockCode",
                    (value) => <div className="w-36">{value}</div>,
                    true,
                    "w-36"
                ),
                sortOrder: currentField === "dockCode" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    "Boxes",
                    "boxes",
                    (value) => <div className="w-24">{value}</div>,
                    true,
                    "w-24"
                ),
                sortOrder: currentField === "boxes" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    "Pallets",
                    "pallets",
                    (value) => <div className="w-24">{value}</div>,
                    true,
                    "w-24"
                ),
                sortOrder: currentField === "pallets" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    <div>
                        Boxes
                        <br />
                        Unload
                        <br />
                        Hours
                    </div>,
                    "boxesUnloadManHours",
                    (value) => <div className="w-20">{value}</div>,
                    true,
                    "w-20"
                ),
                sortOrder: currentField === "boxesUnloadManHours" ? currentOrder : null,
            },
            {
                ...leftColumn(
                    <div>
                        Pallets
                        <br />
                        Unload
                        <br />
                        Hours
                    </div>,
                    "palletsUnloadManHours",
                    (value) => <div className="w-20">{value}</div>,
                    true,
                    "w-20"
                ),
                sortOrder: currentField === "palletsUnloadManHours" ? currentOrder : null,
            },
            {
                ...dateColumn(
                    <div>
                        Unload
                        <br />
                        Complete
                    </div>, "unloadComplete", "w-36"),
                sortOrder: currentField === "unloadComplete" ? currentOrder : null,
            },
        ],
    };

    const handleTableChange = (pagination = {}, filters, sorter) => {
        setList((prev) => ({
            ...prev,
            request: {
                ...prev.request,
                sortFilter: {
                    field: sorter.field || "",
                    order: sorter.order === "ascend" ? "asc" : (sorter.order === "descend" ? "desc" : ""),
                },
                pageFilter: {
                    ...prev.request.pageFilter,
                    size: pagination.pageSize || prev.request.pageFilter.size,
                    num: (pagination.current ?? prev.request.pageFilter.num + 1) - 1,
                },
            }
        }));
    };

    const renderTable = (viewKey) => {
        const columns = columnsMap[viewKey];
        const current = (list.request?.pageFilter?.num ?? 0) + 1;
        const pageSize = list.request?.pageFilter?.size ?? 50;
        const total = shipmentData?.total ?? 0;
        const totalPages = Math.ceil(total / pageSize);

        const currentSorter = {
            field: list.request.sortFilter.field,
            order:
                list.request.sortFilter.order === "asc"
                    ? "ascend"
                    : list.request.sortFilter.order === "desc"
                        ? "descend"
                        : "",
        };

        const onCustomPageChange = (page, size) => {
            handleTableChange({ current: page, pageSize: size }, {}, currentSorter); // Pass current sorter instead of {}.
        };

        const onPageSizeChange = (size) => {
            onCustomPageChange(1, size); // reset to first page when size changes
        };
        return (
            <div className="">
                <Spin spinning={loading}>
                    <Table
                        showSorterTooltip={false}
                        columns={columns}
                        dataSource={
                            loading
                                ? new Array(12).fill(null).map((_, index) => ({ key: index }))
                                : shipmentData?.data?.map((item, index) => ({ ...item, key: index, }))
                        }
                        pagination={false}
                        onChange={handleTableChange}
                        scroll={{ x: "max-content", y: "calc(100vh - 270px)" }}
                        size="small"
                        sticky
                        className="min-h-[calc(100vh - 300px)] h-[calc(100vh - 300px)]"
                        rowClassName={(_, index) =>
                            index % 2 === 0 ? "text-sm" : "text-sm bg-[#f3f3f3]"
                        }
                    />
                    <div className="flex justify-between items-center mt-2">
                        {/* Rows per page selector + info */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Rows per page:</span>
                            <Select
                                value={String(pageSize || 50)}
                                onChange={(val) => onPageSizeChange(Number(val))}
                                size="medium"
                                style={{ width: 80 }}
                            >
                                <Option value="10">10</Option>
                                <Option value="20">20</Option>
                                <Option value="50">50</Option>
                                <Option value="100">100</Option>
                            </Select>
                        </div>

                        {/* Custom buttons */}
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-600">
                                {`${(current - 1) * pageSize + 1}-${Math.min(
                                    current * pageSize,
                                    total,
                                )} of ${total} items`}
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    size="medium"
                                    onClick={() => onCustomPageChange(1, pageSize)}
                                    disabled={current === 1}
                                >
                                    <FontAwesomeIcon icon={faAngleDoubleLeft} />
                                </Button>
                                <Button
                                    size="medium"
                                    onClick={() => onCustomPageChange(current - 1, pageSize)}
                                    disabled={current === 1}
                                >
                                    <FontAwesomeIcon icon={faAngleLeft} />
                                </Button>
                                <Button
                                    size="medium"
                                    onClick={() => onCustomPageChange(current + 1, pageSize)}
                                    disabled={current === totalPages}
                                >
                                    <FontAwesomeIcon icon={faAngleRight} />
                                </Button>
                                <Button
                                    size="medium"
                                    onClick={() => onCustomPageChange(totalPages, pageSize)}
                                    disabled={current === totalPages}
                                >
                                    <FontAwesomeIcon icon={faAngleDoubleRight} />
                                </Button>
                            </div>
                        </div>
                    </div>
                </Spin>
            </div>
        );
    };

    const [exportLoading, setExportLoading] = useState(false);
    const exportToExcel = (type, request) => {
        setExportLoading(true);
        ShipmentService.exportToExcel(type, request)
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                const contentDisposition = response.headers["content-disposition"];
                const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || "Shipments.xlsx";
                link.setAttribute("download", filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                message.success("Excel file downloaded successfully.");
            })
            .catch((error) => {
                console.error("Error exporting to Excel:", error);
                message.error("Failed to download Excel file.");
            }).finally(() => setExportLoading(false));
    };

    return (
        <div className="px-2 relative  w-full h-full">
            <div className="flex justify-between items-center w-full my-2 px-2">
                <div className="flex justify-start items-center font-inter">
                    <span
                        className="!font-medium !text-[#015373] text-[30px] !m-0 pl-4 mt-10"
                    >
                        Containers On Site
                    </span>
                    {/* <Button
            type="link"
            onClick={goBack}
            className="text-md font-medium !text-[#01A4A6] pr-4 pt-2"
          >
            / Supplier
          </Button> */}
                </div>
                <div className="flex justify-between font-inter">
                    <WarehouseFilter warehouses={warehouses} />
                    <AutoComplete
                        className="self-end border w-72 !h-[34px] ml-4"
                        placeholder="Search container"
                        prefix={
                            <FontAwesomeIcon
                                icon={faMagnifyingGlass}
                                className="opacity-70 mr-2"
                            />
                        }
                        options={searchResults.map((item) => ({
                            value: item.containerNum, // what shows in input when selected
                            label: (
                                <div className="p-2">
                                    <span className="font-bold">{item.containerNum}</span>&nbsp;-&nbsp;{item.tabName.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                                </div>
                            ),
                            item,
                        }))}
                        value={searchInput}
                        onChange={handleSearchInputChange}
                        onSelect={(value, option) => {
                            const formattedTabName = option.item.tabName
                                .replace(/([A-Z])/g, " $1") // split camelCase
                                .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter

                            handleContainerSelect(option.item);

                            // Input will show plain text (no bold possible)
                            setSearchInput(`${option.item.containerNum} - ${formattedTabName}`);
                        }}
                        allowClear
                        onClear={handleClearSearchResult}
                        notFoundContent={
                            searchLoading ? <Spin size="small" /> : "No results found"
                        }
                    />
                </div>
            </div>
            <Card className="rounded-3xl shadow-xl bg-white bg-opacity-80">
                <Tabs
                    type="card"
                    tabBarGutter={8}
                    activeKey={activeTab}
                    onChange={(activeKey) => setActiveTab(activeKey)}
                    items={tabs.map((key) => ({
                        key,
                        label: (
                            <>
                                <span className="text-[#015373] font-medium">
                                    {key
                                        .replace(/([A-Z])/g, " $1")
                                        .replace(/^./, (str) => str.toUpperCase())}
                                </span>
                                <span className="px-2 ml-2 rounded-md bg-[#01A4A6] font-semibold text-white">
                                    {counts[key] ?? 0}
                                </span>
                            </>
                        ),
                        children: renderTable(key),
                    }))}
                    tabBarExtraContent={
                        <Dropdown menu={{
                            items: [
                                {
                                    key: 1,
                                    label: <span className="text-sm">
                                        {activeTab.replace(/([A-Z])/g, " $1")
                                            .replace(/^./, (str) => str.toUpperCase())} Containers
                                    </span>,

                                },
                                {
                                    key: 2,
                                    label: <span className="text-sm">
                                        All Containers
                                    </span>,
                                },
                            ],
                            onClick: ({ key }) => {
                                if (key == 2) {
                                    exportToExcel("", list.request);
                                } else {
                                    exportToExcel(activeTab, list.request);
                                }
                            },
                        }}>
                        <AppButton variant="export" loading={exportLoading}>Export</AppButton>
                        </Dropdown>
                    }
                />
                <Modal
                    title="Search Container"
                    open={isSearchModalVisible}
                    onCancel={handleCancelModal}
                    footer={null}
                    destroyOnClose
                >
                    <Input
                        placeholder="Enter container number"
                        value={searchInput}
                        onChange={handleSearchInputChange}
                        onClear={handleClear}
                        allowClear
                    />
                    <Spin spinning={searchLoading}>
                        <div className="mt-2 max-h-96 overflow-auto">
                            {searchResults.length > 0 ? (
                                searchResults.map((item, index) => (
                                    <div
                                        key={index}
                                        className="p-2 hover:bg-gray-200 cursor-pointer border-b"
                                        onClick={() => handleContainerSelect(item)}
                                    >
                                        <div>
                                            <span className="font-bold">{item.containerNum}</span> -{" "}
                                            {item.tabName
                                                .replace(/([A-Z])/g, " $1")
                                                .replace(/^./, (str) => str.toUpperCase())}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-gray-400 mt-4 text-center">
                                    No results found
                                </div>
                            )}
                        </div>
                    </Spin>
                </Modal>
            </Card>
        </div>
    );
};

export default ContainerOnSiteList;